// apps/client/src/ui/screens/townSettings.tsx (M1-T18, spec §4.3, §4.4, §9.11) — the mayor's town settings: instant mail and who can
// make invite links. Each toggle sends `town.settings { patch }` optimistically and is undone on a reject (the reject toasts by itself);
// `town.updated` then brings the same settings to everyone else.
import { t as tr, tuning } from '@whiskerton/content';
import type { TownSettings } from '@whiskerton/shared';
import { useState } from 'react';
import { getGame } from '../../game/instance';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Button, Panel } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { townSettings: undefined } }

type Patch = Partial<Pick<TownSettings, 'instantMail' | 'residentsCanInvite'>>;
/** Same body as M1-T14's: hours reach text only through time.hourAm / time.hourPm (rev 6). */
const hourText = (h: number) => tr(h < 12 ? 'time.hourAm' : 'time.hourPm', { h: h % 12 || 12 });

function withSettings(settings: TownSettings): void {
  const cur = useGame.getState().town;
  if (cur) useGame.setState({ town: { ...cur, settings } });
}
export async function applyTownSettings(patch: Patch): Promise<boolean> {
  const before = useGame.getState().town?.settings;
  if (!before) return false;
  withSettings({ ...before, ...patch });                                        // optimistic
  const res = await getGame().cmd.act('town.settings', { patch });
  if (res.ok) { withSettings(res.r.settings); return true; }
  const now = useGame.getState().town?.settings;
  if (now) withSettings({ ...now, ...Object.fromEntries(Object.keys(patch).map((k) => [k, before[k as keyof Patch]])) });   // undo only what we changed
  return false;
}

function Row({ label, note, checked, disabled, onChange }: { label: string; note: string; checked: boolean; disabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'grid', gap: 4, background: 'var(--cream-100)', borderRadius: 16, padding: '12px 14px' }}>
      <span style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', fontWeight: 900 }}>
        {label}
        <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)}
          style={{ accentColor: 'var(--leaf-700)', width: 26, height: 26, flex: 'none' }} />
      </span>
      <small style={{ fontWeight: 700 }}>{note}</small>
    </label>
  );
}

function TownSettingsScreen({ close }: { close: () => void }) {
  const t = useT();
  const settings = useGame((s) => s.town?.settings ?? null);
  const [busy, setBusy] = useState(false);
  const set = async (patch: Patch) => {
    setBusy(true);
    try { await applyTownSettings(patch); } finally { setBusy(false); }
  };
  const [first, second] = tuning.time.mailRoundHours;
  return (
    <Panel title={t('settings.townTitle')}>
      <div style={{ display: 'grid', gap: 12, width: 420, maxWidth: '100%' }}>
        {settings === null ? <p style={{ margin: 0 }}>{t('common.loading')}</p> : <>
          <Row label={t('settings.instantMail')} note={t('settings.instantMailNote', { first: hourText(first), second: hourText(second) })}
            checked={settings.instantMail} disabled={busy} onChange={(v) => void set({ instantMail: v })} />
          <Row label={t('settings.residentsCanInvite')} note={t('settings.residentsCanInviteNote')}
            checked={settings.residentsCanInvite} disabled={busy} onChange={(v) => void set({ residentsCanInvite: v })} />
        </>}
        <Button variant="quiet" onClick={close}>{t('common.close')}</Button>
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'townSettings'> = {
  id: 'townSettings', layer: 'panel', component: TownSettingsScreen, menu: { label: 'settings.townTitle', order: 91, mayorOnly: true },
};
